import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useCreateCalendar } from '@/hooks/useCalendars'
import {
  calendarHeaderSchema,
  type CalendarHeaderFormValues,
} from '@/components/Calendars/calendarSchemas'

interface CreateCalendarDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const DEFAULT_VALUES: CalendarHeaderFormValues = {
  name: '',
  description: '',
}

export function CreateCalendarDialog({
  open,
  onOpenChange,
}: CreateCalendarDialogProps) {
  const createCalendar = useCreateCalendar()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CalendarHeaderFormValues>({
    resolver: zodResolver(calendarHeaderSchema),
    defaultValues: DEFAULT_VALUES,
  })

  useEffect(() => {
    if (!open) {
      reset(DEFAULT_VALUES)
    }
  }, [open, reset])

  const onSubmit = async (data: CalendarHeaderFormValues) => {
    await createCalendar.mutateAsync({
      name: data.name,
      description: data.description || undefined,
    })
    onOpenChange(false)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={createCalendar.isPending ? undefined : onOpenChange}
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create Calendar</DialogTitle>
          <DialogDescription>
            Fill in the details to create a new calendar.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="Calendar name"
              autoComplete="off"
              {...register('name')}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Optional description..."
              {...register('description')}
            />
            {errors.description && (
              <p className="text-sm text-destructive">
                {errors.description.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createCalendar.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createCalendar.isPending}>
              {createCalendar.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Create'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
